/*
 * One QueryClient per (firmId, asOf) scope.
 *
 * The overall rule: a scope's cache IS its QueryClient. Nothing in a query key
 * identifies the scope, and nothing outside this file constructs a QueryClient.
 *
 * IMPORT RULE: this module and `@/trpc/scope` are imported by their own paths, never
 * through the `@/trpc` barrel, and the barrel does not re-export them — see the header
 * of `scope.ts` for why (the ~123 `vi.mock('@/trpc', …)` factories in the test suite).
 *
 * The QueryClient class comes from `@tanstack/vue-query`, NOT `@tanstack/query-core`.
 * vue-query's subclass sets `isRestoring = ref(false)` in its constructor, and
 * `useBaseQuery` reads that ref to decide whether to subscribe and fetch — a core
 * QueryClient here would leave every query permanently idle.
 *
 * CACHE-RESOLUTION RULE for code with no injection context (stores, plain modules):
 * resolve `getScopedQueryClient()` synchronously at the point an operation starts and
 * hold that reference for the operation's whole life — apply, settle, and rollback
 * alike. Resolving inside a `.then` or inside a rollback closure picks whichever scope
 * happens to be active when the promise settles, which writes one scope's data into
 * another scope's cache.
 */

// library imports
import { nextTick } from 'vue';
import { QueryClient } from '@tanstack/vue-query';

// local imports
import { getScope, scopeKey, setScope } from './scope';

// types
import type { QueryScope } from '@/types/queryScope';

/*
 * gcTime is 30 minutes rather than the library's 5, so a firm A → B → A round trip
 * comes back to a populated cache instead of an empty one; each scope now has its own
 * client, so an unused scope's client sits idle for the whole window rather than being
 * garbage-collected out from under a user who tabs away and back. staleTime stays at
 * 5 minutes — how long data is trusted without a refetch is a freshness question and is
 * unaffected by the partitioning.
 */
export const QUERY_DEFAULTS = {
  queries: {
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  },
};

/* Upper bound on how many scoped clients are kept alive at once. */
const MAX_SCOPED_CLIENTS = 8;

/** One live QueryClient plus the counter value at its most recent resolution. */
interface ScopedClientEntry {
  client: QueryClient;
  scope: QueryScope;
  lastUsed: number;
}

const clients = new Map<string, ScopedClientEntry>();

let useCounter = 0;

/*
 * MOUNT RULE: a client is mounted for its whole life in the map — `mount()` on
 * creation, `unmount()` only when it leaves the map (eviction or clearAllQueryClients),
 * nothing in between. There is no per-transition mount/unmount.
 */
function createClient(scope: QueryScope): QueryClient {
  // A dated client cannot originate a write, and a live write invalidates every dated
  // client for its firm (invalidateDatedScopes below), so the only staleness left over
  // gcTime is another user's write — tolerated everywhere else in the app already.
  const defaultOptions = scope.asOf !== null
    ? { queries: { ...QUERY_DEFAULTS.queries, staleTime: QUERY_DEFAULTS.queries.gcTime } }
    : QUERY_DEFAULTS;

  const client = new QueryClient({ defaultOptions });
  client.mount();

  return client;
}

/**
 * Resolves the QueryClient for a scope, creating and mounting it on a miss.
 *
 * Every call — hit or miss — stamps the entry's recency for the LRU eviction below.
 * Recency has to be use-based rather than activation-based because `getLiveScopeClient`
 * resolves a client the router never activates.
 *
 * @param scope - the scope to resolve; defaults to the active scope
 * @returns the QueryClient owning that scope's cache
 */
export function getScopedQueryClient(scope: QueryScope = getScope()): QueryClient {
  const key = scopeKey(scope);

  let entry = clients.get(key);
  if (!entry) {
    entry = { client: createClient(scope), scope, lastUsed: 0 };
    clients.set(key, entry);
  }

  useCounter += 1;
  entry.lastUsed = useCounter;

  return entry.client;
}

/**
 * Resolves the live (undated) client for the active firm, whatever date the active
 * scope carries. Opt-in per composable for date-invariant reads — lookups, custom-field
 * definitions and the like — so those are fetched once for a firm rather than once per
 * date the user visits. Pass it as vue-query 5.100's positional second argument:
 * `useQuery(opts, getLiveScopeClient())`.
 *
 * @returns the QueryClient for `{ firmId: <active firm>, asOf: null }`
 */
export function getLiveScopeClient(): QueryClient {
  return getScopedQueryClient({ firmId: getScope().firmId, asOf: null });
}

/*
 * Drops the least-recently-resolved clients until the map is back at MAX_SCOPED_CLIENTS.
 *
 * Two clients are pinned and never evicted: the active scope's client and the active
 * firm's live client (they can be the same object). The live pin matters even though
 * every resolution stamps recency: a screen that reads nothing through
 * getLiveScopeClient() while the user browses 8+ dated scopes would let the live
 * client's position go stale, and evicting it throws away data every date shares.
 */
function evictExcessClients(): void {
  if (clients.size <= MAX_SCOPED_CLIENTS)
    return;

  const active = getScope();
  const pinned = new Set([scopeKey(active), scopeKey({ firmId: active.firmId, asOf: null })]);

  const candidates = [...clients.entries()]
    .filter(([key]) => !pinned.has(key))
    .sort((a, b) => a[1].lastUsed - b[1].lastUsed);

  for (const [key, entry] of candidates) {
    if (clients.size <= MAX_SCOPED_CLIENTS)
      break;

    entry.client.clear();
    entry.client.unmount();
    clients.delete(key);
  }
}

/**
 * The only scope transition. Called by the router's `afterEach` once a navigation is
 * confirmed, and by nothing else.
 *
 * Two cancellation passes run against the outgoing client:
 *
 * 1. Synchronous and unfiltered. vue-router sets `currentRoute.value` before `afterEach`
 *    runs and vue-query's options watcher is `flush: 'pre'`, so the outgoing tree — still
 *    mounted until the keyed remount — can start a fetch on the previous client using the
 *    new scope's header. This pass covers everything in flight at the moment of the
 *    confirmed navigation.
 * 2. After `nextTick()`, filtered to queries with no observers. It must NOT be
 *    unfiltered: because `getLiveScopeClient()` exists, the previous client can also be
 *    the client the INCOMING tree reads from (a live → dated transition). vue-query's
 *    `cancelQueries` defaults to `revert: true`, so an unfiltered cancel here would
 *    revert those in-flight fetches and leave live-scope queries stuck at
 *    `status: 'pending'` or showing stale data that never refreshes. "No observers"
 *    separates outgoing-tree queries — already unsubscribed via `onScopeDispose` by the
 *    time the remount flushes — from incoming-tree queries, which have an observer.
 *
 * No `queryFn` in the app forwards an AbortSignal to tRPC, so `cancelQueries` never
 * aborts the underlying HTTP request; the wasted request still completes on the wire.
 * What cancelling buys is that its result is discarded instead of landing in the cache.
 *
 * @param next - the scope to activate
 */
export function activateScope(next: QueryScope): void {
  const prev = getScope();

  // No-op check before resolving any client: resolving first would create and mount a
  // client for the startup sentinel scope { firmId: '', asOf: null } that nothing reads,
  // and stamp recency on same-scope navigations for work discarded one line later.
  if (scopeKey(prev) === scopeKey(next))
    return;

  // Peek, don't create: the outgoing scope only has in-flight queries to cancel if a
  // client already exists for it (the startup sentinel never gets one).
  const prevClient = clients.get(scopeKey(prev))?.client;

  void prevClient?.cancelQueries();

  getScopedQueryClient(next);
  setScope(next);

  if (prevClient)
    void nextTick().then(() => prevClient.cancelQueries({ predicate: (q) => q.getObserversCount() === 0 }));

  evictExcessClients();
}

/**
 * Marks every dated (non-live) scope's cache stale for one firm. Called after a
 * successful mutation, since a write always fires from a live scope and can back-date a
 * transaction to on-or-before a date the user has already visited and cached — without
 * this, that date's cached view would miss the new row until staleTime expired.
 *
 * Every affected client is inactive (a dated client cannot originate a write, so nothing
 * is rendering from it right now), so this marks entries stale and issues no fetch; the
 * next visit to that date refetches. The live client for the firm is untouched.
 *
 * @param firmId - the firm whose dated scopes should be invalidated
 */
export function invalidateDatedScopes(firmId: string): void {
  for (const entry of clients.values()) {
    if (entry.scope.firmId === firmId && entry.scope.asOf !== null)
      void entry.client.invalidateQueries();
  }
}

/**
 * Empties and tears down every scoped client. For sign-out and impersonation, where no
 * cached payload from the previous effective user may survive.
 */
export function clearAllQueryClients(): void {
  for (const entry of clients.values()) {
    entry.client.clear();
    entry.client.unmount();
  }

  clients.clear();
}
